/**
 * edgeCalls.ts — typed wrappers around the EffectiveSLP Edge Functions.
 *
 * These are thin: they hit the same endpoints the iOS app hits, with
 * the same request/response shape. The web client doesn't get any
 * "on-device" code path — Foundation Models is iOS-only — so the
 * always-cloud path here maps to `api-generate-session-plan` and
 * friends. The iOS app's `AIModelPathGate.current()` returns
 * `.alwaysCloud` for ~60-70% of installs; the web client is at
 * 100% always-cloud.
 *
 * Why a separate module: the screen files import these helpers
 * instead of calling `supabase.functions.invoke` directly so we can
 * mock the call surface in unit tests, and so the response type
 * shapes live in one place rather than being inlined at every
 * call site.
 */

// ---------- Errors -----------------------------------------------------------

/**
 * Mirrors the structured error bodies the Edge Functions return.
 * See PRD §6.1 for the canonical error codes.
 */
export interface EdgeFunctionError {
  code:
    | 'unauthenticated'
    | 'ai_consent_required'
    | 'free_tier_exhausted'
    | 'parse_failed'
    | 'rate_limited'
    | 'not_found'
    | 'forbidden'
    | 'server_error';
  message: string;
}

export class EdgeCallError extends Error {
  readonly code: EdgeFunctionError['code'];
  readonly status: number;

  constructor(status: number, body: EdgeFunctionError | { message: string; code?: string }) {
    super(body.message);
    this.name = 'EdgeCallError';
    this.code = (body.code ?? 'server_error') as EdgeFunctionError['code'];
    this.status = status;
  }
}